import { DS2 } from '../../theme/designTokens';

interface Props {
  width:  number;
  height: number;
  /** Vertical offset from the containing (relatively-positioned) section. */
  top: number | string;
  /** Horizontal offset from center, px — Figma shifts each instance right of center by a fixed amount. */
  leftOffset?: number;
  /** Per-instance Figma radius — Landing uses the 342.422px default; Get Started specifies its own. */
  radius?: number;
  /** Per-instance opacity — Figma default 0.25; Home's Friends panel uses 0.12. */
  opacity?: number;
}

/**
 * DS2 atmospheric background glow — the soft radial gold spotlight used
 * behind hero/section content. Confirmed exact formula from Figma's
 * Dev Mode export (node 696:979): a single blurred radial gradient,
 * reused at different sizes/positions per section.
 *
 * `border-radius: 450px` is a Figma-exact literal, NOT `50%` — on a box
 * taller than 900px it would be a smooth ellipse, but at these dimensions
 * the fixed 450px radius gets CSS-clamped on the shorter axis, producing a
 * stadium/pill silhouette (flat top/bottom, semicircular ends) rather than
 * a uniform ellipse. Confirmed identical across all 3 Figma instances.
 *
 * Renders centered horizontally (plus `leftOffset`) within its containing
 * block, which must be `position: relative` (or `absolute`/`fixed`).
 */
export function AtmosphericGlow({ width, height, top, leftOffset = 0, radius = 342.422, opacity }: Props) {
  return (
    <div
      aria-hidden="true"
      style={{
        position:  'absolute',
        top,
        left:      `calc(50% + ${leftOffset}px)`,
        transform: 'translateX(-50%)',
        borderRadius: `${radius}px`,
        ...DS2.glow.formula(width, height),
        ...(opacity != null ? { opacity } : null),
      }}
    />
  );
}
